import { UnisphereRuntimeLoader, UnisphereRuntimeVisual } from "@unisphere/runtime-react";
import { useMemo } from "react";
import { baseUrl, encodeEntryNameForURI, getEntryUrl, serverUrl } from "@kaltura/mediaspace-shared-utils";
import { useConfig } from "@kaltura/mediaspace-shared-data-kms-config";
import { version } from "@kaltura/mediaspace-shared-data-kms-version";
import { ChatRuntimeName, ChatRuntimeSettings, WidgetName } from "@unisphere/genie-core";

export enum CategoryType {
    CHANNEL = "channel",
    GALLERY = "gallery",
}

const genieVisualType = "page";

export interface GenieProps {
    genieServerUrl: string;
    ks: string;
    uiconfId: string;
    categoryData?: { id: number; name: string; type: CategoryType };
}

export const Genie = ({ genieServerUrl, ks, uiconfId, categoryData }: GenieProps) => {
    const {
        application: { partnerId, serviceUrl },
        analytics,
    } = useConfig();

    const getCategoryUrl = () => {
        if (!categoryData) {
            return "";
        }

        switch (categoryData.type) {
            case CategoryType.CHANNEL:
                return `${baseUrl}/channel/${encodeEntryNameForURI(categoryData.name)}/${categoryData.id}`;
            case CategoryType.GALLERY:
                return `${baseUrl}/category/${encodeEntryNameForURI(categoryData.name)}/${categoryData.id}`;
            default:
                return "";
        }
    };

    const settings = useMemo<ChatRuntimeSettings>(
        () => ({
            getSourceUrl: ({ entryId }: { entryId: string; startTime?: number }) => {
                return getEntryUrl(entryId);
            },
            shareUrl: {
                queryParam: "mid",
                createUrl: ({ messageId }: { messageId: string }) => {
                    const host = serverUrl + (baseUrl ? `/${baseUrl}` : "");
                    if (categoryData) {
                        // if in context of category, preserve it in the share url
                        return `${host}/genie/index/index/categoryId/${categoryData.id}?mid=${messageId}`;
                    }
                    return `${host}/genie?mid=${messageId}`;
                },
            },
            genieServerUrl,
            kalturaServerURI: serviceUrl,
            ks,
            partnerId: partnerId.toString(),
            uiconfId,
            analytics: {
                enabled: true,
                serverUrl: analytics.serviceUrl,
                appName: analytics.appName,
                appVersion: version,
            },
            ...(categoryData
                ? {
                      customization: {
                          breadcrumb: {
                              root: {
                                  url: getCategoryUrl(),
                                  label: categoryData.name,
                              },
                          },
                      },
                  }
                : {}),
        }),
        [analytics.appName, analytics.serviceUrl, categoryData, genieServerUrl, ks, partnerId, serviceUrl, uiconfId]
    );

    return (
        <>
            <UnisphereRuntimeLoader
                widgetName={WidgetName}
                runtimeName={ChatRuntimeName}
                runtimeSettings={settings as unknown as Record<string, unknown>}
            />
            <UnisphereRuntimeVisual
                visualSettings={null}
                visualType={genieVisualType}
                widgetName={WidgetName}
                runtimeName={ChatRuntimeName}
            />
        </>
    );
};
